import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RichTextEditor from "../ReusableComponent/RichTextEditor";
import { fetchNoteById, createNote, updateNote, deleteNote, fetchTopicById, fetchModuleById, fetchSubjectById2 } from "../../pages/services/notesService";
import ConfirmationDialog from "../ReusableComponent/ConfirmationDialog";
import "../../styles/CourseNotes.css";

const AddEditNote = ({ mode }) => {
  // Both flow types share topicId and noteId;
  // courseId is present in the course-centric flow (/courses/:courseId/topics/...)
  // subjectId is present in the subject-centric flow (/notes/subjects/:subjectId/topics/...)
  const { courseId, subjectId, topicId, noteId } = useParams();
  const navigate = useNavigate();

  // Determine back paths based on which flow we're in
  const topicPath = subjectId
    ? `/notes/subjects/${subjectId}/topics/${topicId}`
    : `/courses/${courseId}/topics/${topicId}`;

  const [title, setTitle] = useState("");
  const [notesText, setNotesText] = useState("");
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [subjectTitle, setSubjectTitle] = useState("");
  const [moduleTitle, setModuleTitle] = useState("");
  const [topicTitle, setTopicTitle] = useState("");

  useEffect(() => {
    if (mode === "edit" && noteId) {
      fetchNoteById(topicId, noteId)
        .then((data) => { setTitle(data.title); setNotesText(data.notesText || ""); })
        .catch(() => setError("Failed to load note. Please go back and try again."))
        .finally(() => setLoading(false));
    }
    if (subjectId && topicId) {
      fetchTopicById(topicId).then((t) => {
        setTopicTitle(t?.title || "");
        if (t?.moduleId) fetchModuleById(t.moduleId).then((m) => setModuleTitle(m?.title || "")).catch(() => {});
      }).catch(() => {});
      fetchSubjectById2(subjectId).then((s) => setSubjectTitle(s?.title || "")).catch(() => {});
    }
  }, [mode, topicId, noteId, subjectId]);

  const handleSave = async () => {
    if (!title.trim()) { setError("Title is required"); return; }
    setSaving(true);
    setError("");
    try {
      if (mode === "edit") {
        await updateNote(topicId, noteId, { title: title.trim(), notesText });
      } else {
        await createNote(topicId, { title: title.trim(), notesText });
      }
      navigate(topicPath, {
        state: { successMessage: `Note ${mode === "edit" ? "updated" : "created"} successfully` },
      });
    } catch {
      setError("Failed to save note. Please try again.");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteNote(topicId, noteId);
      navigate(topicPath, { state: { successMessage: "Note deleted successfully" } });
    } catch {
      setError("Failed to delete note. Please try again.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="cn-page">
        <div className="cn-loading" style={{ paddingTop: 60 }}>Loading note...</div>
      </div>
    );
  }

  return (
    <div className="cn-page">
      <ConfirmationDialog
        show={showDeleteDialog}
        type="delete"
        message="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Breadcrumb — only for subject-centric flow */}
      {subjectId && (
        <div className="cn-breadcrumb">
          <span className="cn-breadcrumb-link" onClick={() => navigate("/notes")}>Notes</span>
          {subjectTitle && (
            <>
              <span className="cn-breadcrumb-sep"> › </span>
              <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
                Subject: {subjectTitle}
              </span>
            </>
          )}
          {moduleTitle && (
            <>
              <span className="cn-breadcrumb-sep"> › </span>
              <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
                Module: {moduleTitle}
              </span>
            </>
          )}
          {topicTitle && (
            <>
              <span className="cn-breadcrumb-sep"> › </span>
              <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}/topics/${topicId}`)}>
                Topic: {topicTitle}
              </span>
            </>
          )}
          <span className="cn-breadcrumb-sep"> › </span>
          <span className="cn-breadcrumb-current">{mode === "edit" ? "Edit Note" : "Add Note"}</span>
        </div>
      )}

      <div className="cn-header">
        <h2 className="cn-title">{mode === "edit" ? "Edit Note" : "Add Note"}</h2>
        <button className="cn-back-btn" onClick={() => navigate(-1)} disabled={saving || deleting}>
          ← Back
        </button>
      </div>

      <div className="cn-form-card">
        {error && <div className="cn-error">{error}</div>}

        <div className="cn-form-field">
          <label className="cn-form-label">Title</label>
          <input
            className="cn-form-input"
            placeholder="Enter note title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="cn-form-field">
          <label className="cn-form-label">Content</label>
          <RichTextEditor
            value={notesText}
            onBlur={(val) => setNotesText(val)}
            toolbar="full"
            height={520}
            placeholder="Write your notes here..."
          />
        </div>

        <div className="cn-form-actions">
          {mode === "edit" && (
            <button
              className="cn-delete-btn-lg"
              onClick={() => setShowDeleteDialog(true)}
              disabled={saving || deleting}
            >
              {deleting ? "Deleting..." : "Delete Note"}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            className="cn-cancel-btn-lg"
            onClick={() => navigate(-1)}
            disabled={saving || deleting}
          >
            Cancel
          </button>
          <button
            className="cn-save-btn-lg"
            onClick={handleSave}
            disabled={saving || deleting}
          >
            {saving ? "Saving..." : mode === "edit" ? "Update Note" : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditNote;
